import React, { useEffect, useState } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    makeStyles,
    Theme,
    Fade,
    IconButton,
} from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import { IEditableRow } from './types';
import { Save } from 'react-feather';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { DATE_FORMAT, PLACEHOLDER_DATE_NONE } from '../constants';
import { TYPEOF_STRING } from 'src/constants/utils/typeof';

const useStyles = makeStyles((theme: Theme) => ({
    fontWeightMedium: {
        fontWeight: 500,
        backgroundColor: theme.palette.background.paper,
        width: 150,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
    },
    datePicker: {
        color: theme.palette.text.secondary,
        fontSize: 16,
        width: '85%',
        '& .MuiInputBase-input': {
            color: theme.palette.text.secondary,
            fontSize: 16,
        },
        '& .MuiInput-underline:before': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:after': {
            borderBottom: 'none',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: 'none',
        },
        '& .MuiInputBase-root': {
            '&:before': {
                borderBottom: 'none',
            },
            '&:after': {
                borderBottom: 'none',
            },
            '&:hover:not(.Mui-disabled):before': {
                borderBottom: 'none',
            },
        },
    },
    save: {
        marginLeft: 10,
        padding: 8,
    },
}));

export const EditableDateRow = ({
    item,
    loading,
    handleSave,
    ...props
}: IEditableRow) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const handleDateChange = (date: Moment | null) => {
        setSelectedDate(date);
    };

    useEffect(() => {
        if (
            item.text &&
            typeof item.text === TYPEOF_STRING &&
            item.text !== PLACEHOLDER_DATE_NONE
        ) {
            const momentDate = moment(`${item.text}`, DATE_FORMAT);
            if (momentDate.isValid()) {
                setSelectedDate(momentDate);
            }
        } else {
            setSelectedDate(null);
        }
    }, [item.text]);

    const handleValidate = (value: Moment | null) => {
        if (item.validate) return item.validate(value ? value.toDate() : null);
        return true;
    };

    const handleSubmit = () => {
        if (item.field) {
            if (handleValidate(selectedDate)) {
                const dateValue = selectedDate
                    ? selectedDate.toISOString()
                    : null;
                handleSave({ [item.field]: dateValue });
            } else {
                enqueueSnackbar(t('SnackBar.ValidValue'), {
                    variant: 'error',
                });
            }
        }
    };

    return (
        <TableRow {...props}>
            <TableCell className={classes.fontWeightMedium}>
                {item.header}
            </TableCell>
            <Fade in={!loading} timeout={600}>
                <TableCell className={classes.paper}>
                    <Box display="flex" alignItems="center">
                        <DateTimePicker
                            value={selectedDate}
                            onChange={handleDateChange}
                            format={DATE_FORMAT}
                            ampm={true}
                            inputProps={{
                                'aria-label': 'naked',
                            }}
                            className={classes.datePicker}
                            placeholder={t(PLACEHOLDER_DATE_NONE)}
                            clearable
                        />

                        <IconButton
                            className={classes.save}
                            onClick={handleSubmit}
                        >
                            <Save size={20} />
                        </IconButton>
                    </Box>
                </TableCell>
            </Fade>
        </TableRow>
    );
};
